<template>
  <div class="simulator-panel">
    <section class="panel-header">
      <div>
        <h3>Ford 模拟诊断请求</h3>
        <p>旧工具 exe 与新工具 funcEx 请求的前端参考面板</p>
      </div>

      <el-tag effect="plain" type="warning">前端参考</el-tag>
    </section>

    <div class="simulator-layout">
      <section class="config-section request-section">
        <div class="section-heading">
          <div>
            <h4>请求配置</h4>
            <p>{{ form.engine === 'legacy' ? '旧工具消息体为 text/plain' : '新工具消息体为 JSON' }}</p>
          </div>
          <el-tag effect="plain">{{ engineLabel }}</el-tag>
        </div>

        <el-form label-position="top" class="sim-form">
          <el-form-item label="请求服务器">
            <el-radio-group v-model="form.server" class="server-group">
              <el-radio-button
                v-for="server in servers"
                :key="server.key"
                :label="server.key"
              >
                {{ server.label }}
              </el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="工具类型">
            <el-radio-group v-model="form.engine" class="engine-group">
              <el-radio-button label="new">新工具</el-radio-button>
              <el-radio-button label="legacy">旧工具</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="接口模板">
            <el-select v-model="form.templateKey" filterable class="full-control">
              <el-option
                v-for="template in templates"
                :key="template.key"
                :label="`${template.funcType} ${template.title}`"
                :value="template.key"
              >
                <div class="template-option">
                  <strong>{{ template.funcType }} {{ template.title }}</strong>
                  <small>{{ template.desc }}</small>
                </div>
              </el-option>
            </el-select>
          </el-form-item>

          <div class="field-grid">
            <el-form-item label="序列号">
              <el-input v-model.trim="form.sn" placeholder="989340000121" clearable />
            </el-form-item>

            <el-form-item label="VIN">
              <el-input v-model.trim="form.vin" placeholder="1FA6P8CF5R5407760" clearable />
            </el-form-item>

            <el-form-item label="软件版本">
              <el-input v-model.trim="form.diagVersion" placeholder="V50.75" clearable />
            </el-form-item>

            <el-form-item label="软件包 ID">
              <el-input v-model.trim="form.diagId" placeholder="USAFORD" clearable />
            </el-form-item>

            <el-form-item label="语言">
              <el-select v-model="form.language" class="full-control">
                <el-option label="EN" value="EN" />
                <el-option label="CN" value="CN" />
                <el-option label="HK" value="HK" />
              </el-select>
            </el-form-item>
          </div>

          <el-form-item label="Parm JSON">
            <el-input
              v-model="parmText"
              type="textarea"
              :rows="8"
              resize="vertical"
              class="json-input"
              spellcheck="false"
            />
          </el-form-item>

          <div class="form-actions">
            <el-button :icon="Refresh" plain @click="resetTemplateParm">重置参数</el-button>
            <el-button :icon="DocumentChecked" plain @click="formatParm">格式化 JSON</el-button>
            <el-button type="primary" :icon="VideoPlay" @click="generatePreview">生成预览</el-button>
            <el-button
              type="success"
              :icon="Promotion"
              :loading="sending"
              @click="sendSimulatorRequest"
            >
              发送请求
            </el-button>
          </div>
        </el-form>
      </section>

      <section class="config-section preview-section">
        <div class="section-heading">
          <div>
            <h4>请求预览</h4>
            <p>{{ endpointText }}</p>
          </div>
          <el-button :icon="CopyDocument" plain size="small" @click="copyPreview">
            复制
          </el-button>
        </div>

        <div class="route-strip">
          <span>{{ form.engine === 'legacy' ? 'text' : 'json' }}</span>
          <strong>{{ routeText }}</strong>
        </div>

        <pre class="json-preview">{{ previewJsonText }}</pre>
      </section>
    </div>

    <section class="config-section result-section">
      <div class="section-heading">
        <div>
          <h4>模拟输出</h4>
          <p>后续接入后台后展示 stdout、统一 JSON 响应和耗时</p>
        </div>
        <el-button plain @click="clearResult">清空</el-button>
      </div>

      <el-table :data="resultRows" border size="small" class="result-table">
        <el-table-column prop="name" label="字段" width="150" />
        <el-table-column prop="value" label="值" min-width="220">
          <template #default="{ row }">
            <span class="mono">{{ row.value }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="note" label="说明" min-width="240" />
      </el-table>

      <pre v-if="runResultText" class="json-preview result-preview">{{ runResultText }}</pre>
    </section>
  </div>
</template>

<script setup>
import { computed, onActivated, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import {
  CopyDocument,
  DocumentChecked,
  Promotion,
  Refresh,
  VideoPlay,
} from '@element-plus/icons-vue';

import { runFordDiagnosticSimulator } from '@/api/users';
import {
  buildRequestPreview,
  buildSimulatorRunRequest,
  buildSimulatorPayload,
  getSimulatorDefaults,
  getSimulatorServer,
  getSimulatorTemplate,
  listSimulatorServers,
  listSimulatorTemplates,
  normalizeJsonText,
} from '@/utils/fordDiagnosticSimulatorUi.mjs';

const templates = ref(listSimulatorTemplates());
const servers = listSimulatorServers();
const defaults = getSimulatorDefaults();

const form = reactive({
  server: defaults.server,
  engine: 'new',
  templateKey: defaults.templateKey,
  sn: defaults.sn,
  vin: defaults.vin,
  diagVersion: defaults.diagVersion,
  diagId: defaults.diagId,
  language: defaults.language,
});

const parmText = ref(formatTemplateParm(form.templateKey));
const generatedPayload = ref('');
const runResultText = ref('');
const sending = ref(false);

const currentTemplate = computed(() => getSimulatorTemplate(form.templateKey));
const currentServer = computed(() => getSimulatorServer(form.server));

const engineLabel = computed(() => (form.engine === 'legacy' ? '旧工具 exe' : '新工具 funcEx'));

const endpointText = computed(() => {
  if (form.engine === 'legacy') {
    return `${currentServer.value.serviceBase}/services/v2/runcmd/tool_GetIVSData.exe`;
  }

  return `${currentServer.value.apiBase}/v1/funcEx/${currentTemplate.value.funcPath}`;
});

const routeText = computed(() => {
  if (form.engine === 'legacy') {
    try {
      return buildPayload().backendExeCommand;
    } catch (error) {
      return `tool_GetIVSData.exe ${currentTemplate.value.funcType} 1 1 $AUTH$ <sn> $CC$ $IP$ <vehicle-data>`;
    }
  }

  return `${currentTemplate.value.method} ${endpointText.value}`;
});

const previewJsonText = computed(() => {
  if (generatedPayload.value) {
    return generatedPayload.value;
  }

  try {
    return buildRequestPreview(buildFormOptions());
  } catch (error) {
    return error.message;
  }
});

const resultRows = computed(() => {
  const status = sending.value
    ? 'sending'
    : (runResultText.value ? 'sent' : (generatedPayload.value ? 'ready' : 'draft'));
  return [
    {
      name: 'server',
      value: currentServer.value.label,
      note: `${currentServer.value.key.toUpperCase()} 域名`,
    },
    {
      name: 'engine',
      value: form.engine,
      note: form.engine === 'legacy' ? '调用 exe 并读取控制台输出' : '构造 dJson 后复用 VehicleService',
    },
    {
      name: 'target',
      value: endpointText.value,
      note: form.engine === 'legacy' ? '前端传 text/plain，后端拆分后执行 exe' : '前端传 JSON，后端使用网站 Authorization',
    },
    {
      name: 'funcType',
      value: String(currentTemplate.value.funcType),
      note: form.engine === 'legacy' ? '旧消息体第一个 / 分段' : '新工具 funcEx 对应类型',
    },
    {
      name: 'auth',
      value: 'Authorization',
      note: '不再传 cc/sign，使用网站登录 token',
    },
    {
      name: 'status',
      value: status,
      note: runResultText.value ? '已收到后端模拟结果' : (generatedPayload.value ? '预览已生成' : '当前为实时草稿'),
    },
  ];
});

function formatTemplateParm(templateKey) {
  return JSON.stringify(getSimulatorTemplate(templateKey).parm, null, 2);
}

function refreshTemplates() {
  templates.value = listSimulatorTemplates();

  if (!templates.value.some((template) => template.key === form.templateKey)) {
    form.templateKey = templates.value[0]?.key || defaults.templateKey;
    parmText.value = formatTemplateParm(form.templateKey);
  }

  generatedPayload.value = '';
  runResultText.value = '';
}

function buildFormOptions() {
  return {
    ...form,
    parmText: parmText.value,
  };
}

function buildPayload() {
  return buildSimulatorPayload(buildFormOptions());
}

function resetTemplateParm() {
  parmText.value = formatTemplateParm(form.templateKey);
  generatedPayload.value = '';
  runResultText.value = '';
}

function formatParm() {
  try {
    parmText.value = normalizeJsonText(parmText.value);
    ElMessage.success('JSON 已格式化');
  } catch (error) {
    ElMessage.warning(error.message);
  }
}

function generatePreview() {
  try {
    generatedPayload.value = buildRequestPreview(buildFormOptions());
    ElMessage.success('请求预览已生成');
  } catch (error) {
    ElMessage.warning(error.message);
  }
}

function clearResult() {
  generatedPayload.value = '';
  runResultText.value = '';
}

async function sendSimulatorRequest() {
  if (sending.value) {
    return;
  }

  try {
    const options = buildFormOptions();
    const runRequest = buildSimulatorRunRequest(options);
    generatedPayload.value = buildRequestPreview(options);
    runResultText.value = '';
    sending.value = true;

    const response = await runFordDiagnosticSimulator(form.server, runRequest);
    runResultText.value = JSON.stringify(response.data, null, 2);
    ElMessage.success('模拟请求已返回');
  } catch (error) {
    const message = error?.info || error?.message || '发送失败';
    runResultText.value = JSON.stringify({
      success: false,
      message,
    }, null, 2);
    ElMessage.warning(message);
  } finally {
    sending.value = false;
  }
}

async function copyPreview() {
  try {
    await navigator.clipboard.writeText(previewJsonText.value);
    ElMessage.success('预览已复制');
  } catch (error) {
    ElMessage.warning('复制失败');
  }
}

watch(
  () => form.templateKey,
  (nextKey) => {
    parmText.value = formatTemplateParm(nextKey);
    generatedPayload.value = '';
    runResultText.value = '';
  },
);

watch(
  () => [
    form.server,
    form.engine,
    form.sn,
    form.vin,
    form.diagVersion,
    form.diagId,
    form.language,
    parmText.value,
  ],
  () => {
    generatedPayload.value = '';
    runResultText.value = '';
  },
);

onMounted(() => {
  window.addEventListener('ford-simulator-templates-changed', refreshTemplates);
});

onActivated(() => {
  refreshTemplates();
});

onBeforeUnmount(() => {
  window.removeEventListener('ford-simulator-templates-changed', refreshTemplates);
});
</script>

<style scoped>
.simulator-panel {
  display: grid;
  gap: 14px;
  min-width: 0;
}

.panel-header,
.config-section {
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(31, 56, 88, 0.05);
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
}

.panel-header h3,
.section-heading h4 {
  margin: 0;
  color: #132033;
  font-weight: 800;
  letter-spacing: 0;
}

.panel-header h3 {
  font-size: 18px;
}

.panel-header p,
.section-heading p {
  margin: 5px 0 0;
  color: #6b7b8f;
  font-size: 13px;
  line-height: 1.45;
}

.simulator-layout {
  display: grid;
  grid-template-columns: minmax(360px, 0.95fr) minmax(0, 1.05fr);
  gap: 14px;
  align-items: start;
}

.config-section {
  min-width: 0;
  padding: 16px;
}

.section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.section-heading h4 {
  font-size: 16px;
}

.sim-form :deep(.el-form-item) {
  margin-bottom: 14px;
}

.server-group,
.engine-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.server-group :deep(.el-radio-button__inner),
.engine-group :deep(.el-radio-button__inner) {
  min-width: 96px;
  height: 36px;
  border: 1px solid #d8e5f2;
  border-radius: 8px !important;
  box-shadow: none;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.full-control {
  width: 100%;
}

.template-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.template-option strong,
.template-option small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-option small {
  color: #7a8a9d;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 12px;
}

.json-input :deep(textarea) {
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
  line-height: 1.45;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.route-strip {
  min-height: 42px;
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  padding: 0 12px;
  margin-bottom: 12px;
  border: 1px solid #d8e9fb;
  border-radius: 8px;
  background: #f2f8ff;
  color: #31485f;
}

.route-strip span {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  border-radius: 6px;
  background: #ffffff;
  color: #0f766e;
  font-size: 12px;
  font-weight: 800;
}

.route-strip strong {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}

.json-preview {
  min-height: 418px;
  max-height: 560px;
  margin: 0;
  padding: 14px;
  overflow: auto;
  border: 1px solid #dfe8f3;
  border-radius: 8px;
  background: #101827;
  color: #dbeafe;
  font-family: Consolas, Monaco, monospace;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.result-preview {
  min-height: 220px;
  max-height: 420px;
  margin-top: 12px;
}

.result-table {
  width: 100%;
}

.result-table :deep(.el-table__header th) {
  background: #f6f9fc !important;
  color: #4b5f75;
  font-weight: 800;
}

.mono {
  font-family: Consolas, Monaco, monospace;
  color: #132033;
}

@media (max-width: 1180px) {
  .simulator-layout {
    grid-template-columns: 1fr;
  }

  .json-preview {
    min-height: 320px;
  }
}

@media (max-width: 640px) {
  .panel-header,
  .section-heading {
    flex-direction: column;
  }

  .field-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    justify-content: flex-start;
  }

  .route-strip {
    grid-template-columns: 1fr;
    padding: 10px;
  }

  .route-strip span {
    width: 72px;
  }
}
</style>
